// src/page/SettingTransit.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';

export default function SettingTransit() {
  useAuthGuard();
  const navigate = useNavigate();

  // Data
  // item shape: {settingId, settingName, settingValue, publishedFlag, launcherMode}
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [saving, setSaving] = useState(false);

  // Validation errors: { [settingId]: { name?: string, url?: string } }
  const [errors, setErrors] = useState({});

  // Load settings
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setFetchError('');
        const token = localStorage.getItem('access_token') || '';
        const res = await fetch(`${API_BASE_URL}/v1/setting/guide`, {
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          signal: ac.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data?.items) ? data.items : [];
        // default launcherMode = '1' (WebView) if missing
        setItems(list.map(r => ({
          ...r,
          launcherMode: (r.launcherMode === '1' || r.launcherMode === '2') ? r.launcherMode : '1'
        })));
      } catch (e) {
        if (e.name !== 'AbortError') setFetchError('設定の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, []);

  // Local update helper
  const updateItem = useCallback((id, patch) => {
    setItems(prev => prev.map(row => (row.settingId === id ? { ...row, ...patch } : row)));
    // clear error for this field if user edits
    setErrors(prev => {
      const next = { ...prev };
      if (next[id]) {
        const updated = { ...next[id] };
        if ('settingName' in patch) delete updated.name;
        if ('settingValue' in patch) delete updated.url;
        if (Object.keys(updated).length === 0) delete next[id];
        else next[id] = updated;
      }
      return next;
    });
  }, []);

  // Validate: require Title & URL when publishedFlag = true
  const runValidation = (list) => {
    const nextErrors = {};
    let firstId = null;
    for (const it of list) {
      if (it.publishedFlag) {
        const n = (it.settingName || '').trim();
        const u = (it.settingValue || '').trim();
        const e = {};
        if (!n) e.name = '公開の場合はタイトル必須です';
        if (!u) e.url = '公開の場合はURL必須です';
        if (Object.keys(e).length) {
          nextErrors[it.settingId] = e;
          if (!firstId) firstId = it.settingId;
        }
      }
    }
    return { ok: Object.keys(nextErrors).length === 0, nextErrors, firstId };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { ok, nextErrors, firstId } = runValidation(items);
    setErrors(nextErrors);
    if (!ok) {
      const el = document.querySelector(`[data-row-id="${firstId}"]`);
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('access_token') || '';
      const payload = {
        items: items.map(it => ({
          settingId: it.settingId,
          settingName: (it.settingName || '').trim(),
          settingValue: (it.settingValue || '').trim(),
          publishedFlag: !!it.publishedFlag,
          launcherMode: it.launcherMode === '2' ? '2' : '1', // only '1' or '2'
        }))
      };
      const res = await fetch(`${API_BASE_URL}/v1/setting/guide`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(t || `HTTP ${res.status}`);
      }
      alert('更新しました');
    } catch (e2) {
      alert(e2?.message || '更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <>
        <nav className="mb-3" aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item"><a href="/setting">サービス管理</a></li>
            <li className="breadcrumb-item active">ガイド・規約管理</li>
          </ol>
        </nav>

        <form className="mb-9" noValidate onSubmit={handleSubmit}>
          <div className="row g-3 flex-between-end mb-5">
            <div className="col-auto">
              <h2 className="mb-2">ガイド・規約管理</h2>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0"
                type="button"
                onClick={() => navigate('/setting')}
                disabled={saving}
              >
                キャンセル
              </button>
              <button
                className="btn btn-primary me-2 mb-2 mb-sm-0"
                type="submit"
                disabled={saving}
              >
                {saving ? '更新中…' : '更新'}
              </button>
            </div>
          </div>

          <div className="row g-5">
            <div className="col-12 col-xl-12">
              {/* Header row */}
              <div className="row">
                <div className="col-md-3">
                  <div className="mb-2">
                    <h4 className="mb-0">タイトル</h4>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-2">
                    <h4 className="mb-0">URL</h4>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-2">
                    <h4 className="mb-0">公開設定</h4>
                  </div>
                </div>
              </div>

              {loading && <div className="text-body-secondary mt-2">読み込み中...</div>}
              {!loading && fetchError && <div className="text-danger mt-2">{fetchError}</div>}

              {!loading && !fetchError && items.map((row) => {
                const e = errors[row.settingId] || {};
                return (
                  <div className="row align-items-start" key={row.settingId} data-row-id={row.settingId}>
                    {/* Title */}
                    <div className="col-md-3">
                      <div className="mb-3">
                        <input
                          className={`form-control ${e.name ? 'is-invalid' : ''}`}
                          type="text"
                          value={row.settingName || ''}
                          onChange={(evt) => updateItem(row.settingId, { settingName: evt.target.value })}
                          placeholder="例）経路検索"
                        />
                        {e.name && <div className="invalid-feedback d-block">{e.name}</div>}
                      </div>
                    </div>
                    {/* URL + launcher buttons inline */}
                    <div className="col-md-6">
                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            className={`form-control ${e.url ? 'is-invalid' : ''}`}
                            type="text"
                            value={row.settingValue || ''}
                            onChange={(evt) => updateItem(row.settingId, { settingValue: evt.target.value })}
                            placeholder="https://example.com"
                            name={`url_${row.settingId}`}
                            aria-invalid={!!e.url}
                          />
                          <button
                            type="button"
                            className={`btn ${row.launcherMode === '1' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => updateItem(row.settingId, { launcherMode: '1' })}
                            disabled={saving}
                            title="WebView"
                          >
                            WebView
                          </button>
                          <button
                            type="button"
                            className={`btn ${row.launcherMode === '2' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => updateItem(row.settingId, { launcherMode: '2' })}
                            disabled={saving}
                            title="ブラウザ"
                          >
                            ブラウザ
                          </button>
                        </div>
                        {/* place feedback outside input-group to avoid DOM swaps that blur the input */}
                        {e.url && <div className="invalid-feedback d-block">{e.url}</div>}
                      </div>
                    </div>
                    {/* Publish radios */}
                    <div className="col-md-3">
                    <div className="mb-3">
                      <div className="d-flex align-items-center gap-3">
                        <label className="mb-0">
                          <input
                            type="radio"
                            name={`pub_${row.settingId}`}
                            value="0"
                            checked={!row.publishedFlag}
                            onChange={() => updateItem(row.settingId, { publishedFlag: false })}
                          />{' '}
                          非公開
                        </label>
                        <label className="mb-0">
                          <input
                            type="radio"
                            name={`pub_${row.settingId}`}
                            value="1"
                            checked={!!row.publishedFlag}
                            onChange={() => updateItem(row.settingId, { publishedFlag: true })}
                          />{' '}
                          公開
                        </label>
                      </div>
                    </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </form>
      </>
    </Layout>
  );
}
